import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import {
  Button,
  ChevronDownIcon,
  ChevronRightIcon,
  CollapseAllIcon,
  ExpandAllIcon,
  FolderIcon,
  SearchIcon,
  EyeIcon,
} from '../../../components/ui';
import { Modal, ModalContent, ModalFooter, ModalHeader, ModalTitle } from '../../../components/ui/Modal';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import {
  getCoaTreeCollapseStorageKey,
  loadPersistedTreeCollapsedIds,
  persistTreeCollapsedIds,
} from '../../../lib/listStatePersistence';
import { fetcher } from '../../../services/fetcher';
import { SpareItemDetailModal } from './SpareItemDetailModal';
import { buildListQuery, normalizePaginatedListResponse, type PaginatedApiResponse } from '../../../services/paginatedList';
import {
  buildSpareCategoryTree,
  useSpareCategoryTree,
  type SpareCategoryTreeNode,
} from '../spare-categories/useData';
import { moduleConfig } from './config';
import {
  collectCollapsedIdsBelowRoot,
  collectExpandableCategoryIds,
  countDirectItemsInCategory,
  filterCategoryTree,
  filterItemsForCategory,
  findCategoryNodeById,
  flattenCategoryTree,
  getCategoryPath,
  type CategoryTreeRow,
} from './treeListUtils';
import type { SpareItem } from './useData';

type Library = { id: number; name: string };

const PANEL_WIDTH_KEY = `spare-items-picker-panel-width`;
const INCLUDE_SUBCATS_KEY = `spare-items-picker-include-subcats`;
const PANEL_MIN = 220;
const PANEL_MAX = 420;
const PANEL_DEFAULT = 300;

const selectedCategoryStorageKey = (libraryId: number) =>
  `spare-items-picker-selected-category:${libraryId}`;

const selectedLibraryStorageKey = () => `spare-items-picker-selected-library`;

const loadPanelWidth = (): number => {
  try {
    const raw = localStorage.getItem(PANEL_WIDTH_KEY);
    const n = raw != null ? Number(raw) : PANEL_DEFAULT;
    if (!Number.isFinite(n)) return PANEL_DEFAULT;
    return Math.min(PANEL_MAX, Math.max(PANEL_MIN, n));
  } catch {
    return PANEL_DEFAULT;
  }
};

const loadIncludeSubcats = (): boolean => {
  try {
    const raw = localStorage.getItem(INCLUDE_SUBCATS_KEY);
    if (raw === null) return true;
    return raw === '1';
  } catch {
    return true;
  }
};

const loadSelectedLibraryId = (): number | null => {
  try {
    const raw = sessionStorage.getItem(selectedLibraryStorageKey());
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
};

const loadSelectedCategoryId = (libraryId: number | null): number | null => {
  if (libraryId == null) return null;
  try {
    const raw = sessionStorage.getItem(selectedCategoryStorageKey(libraryId));
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
};

const resolveInitialCollapsedIds = (libraryId: number, tree: SpareCategoryTreeNode[]): Set<number> => {
  const storageKey = getCoaTreeCollapseStorageKey('spare-items-picker', libraryId);
  const persisted = loadPersistedTreeCollapsedIds(storageKey);
  if (persisted) return persisted;
  return new Set(collectCollapsedIdsBelowRoot(tree));
};

const FlagChip: React.FC<{ label: string; tone: 'warn' | 'danger' }> = ({ label, tone }) => {
  const { colors } = useTheme();
  return (
    <span
      className={cn(
        'inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
        tone === 'warn' && colors.warning,
        tone === 'danger' && colors.error,
      )}
    >
      {label}
    </span>
  );
};

export type SpareItemPickerModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Item ids already on the form (excluding the target line). */
  excludeItemIds?: Set<string>;
  /** Multi-insert selected rows (duplicates already filtered). */
  onInsert: (items: SpareItem[]) => void;
};

export const SpareItemPickerModal: React.FC<SpareItemPickerModalProps> = ({
  isOpen,
  onClose,
  excludeItemIds,
  onInsert,
}) => {
  const { colors } = useTheme();
  const selectAllRef = useRef<HTMLInputElement>(null);

  const { data: librariesRaw } = useSWR<Library[] | { data: Library[] }>(
    isOpen ? '/spare-libraries?page=1&limit=500' : null,
    fetcher,
  );
  const libraries = useMemo(
    () => (Array.isArray(librariesRaw) ? librariesRaw : (librariesRaw?.data ?? [])),
    [librariesRaw],
  );

  const [libraryId, setLibraryId] = useState<number | null>(() => loadSelectedLibraryId());
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [includeSubcats, setIncludeSubcats] = useState(loadIncludeSubcats);
  const [panelWidth, setPanelWidth] = useState(loadPanelWidth);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(() =>
    loadSelectedCategoryId(loadSelectedLibraryId()),
  );
  const [collapsedIds, setCollapsedIds] = useState<Set<number>>(new Set());
  const [treeCollapseReadyForLibrary, setTreeCollapseReadyForLibrary] = useState<number | null>(null);
  const [selectedItems, setSelectedItems] = useState<SpareItem[]>([]);
  const [viewItemId, setViewItemId] = useState<number | null>(null);

  const resizingRef = useRef(false);
  const panelWidthRef = useRef(panelWidth);
  const splitRef = useRef<HTMLDivElement>(null);
  const [panelResizing, setPanelResizing] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSearchTerm('');
    setCategoryFilter('');
    setSelectedItems([]);
    setViewItemId(null);
  }, [isOpen]);

  useEffect(() => {
    if (libraryId != null) return;
    if (libraries.length === 0) return;
    setLibraryId(libraries[0].id);
  }, [libraries, libraryId]);

  const { data: flatCategories = [], isLoading: catsLoading } = useSpareCategoryTree(
    isOpen ? libraryId : null,
  );

  const categoryTree = useMemo(() => buildSpareCategoryTree(flatCategories), [flatCategories]);
  const filteredCategoryTree = useMemo(
    () => filterCategoryTree(categoryTree, categoryFilter),
    [categoryTree, categoryFilter],
  );
  const isCategoryFilterActive = categoryFilter.trim().length > 0;

  const itemsQuery = useMemo(() => {
    if (!isOpen || libraryId == null) return null;
    const q = buildListQuery(undefined, 1, 2000, {
      filters: { filters: { libraryId: [String(libraryId)] } },
    });
    return `${moduleConfig.module.api.list}${q}`;
  }, [isOpen, libraryId]);

  const { data: itemsRaw, isLoading: itemsLoading } = useSWR<PaginatedApiResponse<SpareItem> | SpareItem[]>(
    itemsQuery,
    fetcher,
    { revalidateOnFocus: false },
  );

  const libraryItems = useMemo(() => {
    const normalized = normalizePaginatedListResponse<SpareItem>(itemsRaw, 1, 2000);
    return normalized.data;
  }, [itemsRaw]);

  useEffect(() => {
    if (libraryId == null) return;
    setTreeCollapseReadyForLibrary(null);
    setCollapsedIds(new Set());
    setSelectedCategoryId(loadSelectedCategoryId(libraryId));
  }, [libraryId]);

  useEffect(() => {
    if (libraryId == null || catsLoading || categoryTree.length === 0) return;
    if (treeCollapseReadyForLibrary === libraryId) return;
    setCollapsedIds(resolveInitialCollapsedIds(libraryId, categoryTree));
    setTreeCollapseReadyForLibrary(libraryId);
  }, [libraryId, catsLoading, categoryTree, treeCollapseReadyForLibrary]);

  useEffect(() => {
    if (libraryId == null) return;
    const storageKey = getCoaTreeCollapseStorageKey('spare-items-picker', libraryId);
    persistTreeCollapsedIds(storageKey, collapsedIds);
  }, [libraryId, collapsedIds]);

  useEffect(() => {
    try {
      if (libraryId == null) sessionStorage.removeItem(selectedLibraryStorageKey());
      else sessionStorage.setItem(selectedLibraryStorageKey(), String(libraryId));
    } catch {
      /* ignore */
    }
  }, [libraryId]);

  useEffect(() => {
    if (libraryId == null) return;
    try {
      if (selectedCategoryId == null) sessionStorage.removeItem(selectedCategoryStorageKey(libraryId));
      else sessionStorage.setItem(selectedCategoryStorageKey(libraryId), String(selectedCategoryId));
    } catch {
      /* ignore */
    }
  }, [libraryId, selectedCategoryId]);

  useEffect(() => {
    try {
      localStorage.setItem(INCLUDE_SUBCATS_KEY, includeSubcats ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [includeSubcats]);

  useEffect(() => {
    panelWidthRef.current = panelWidth;
  }, [panelWidth]);

  useEffect(() => {
    if (categoryTree.length === 0) {
      if (selectedCategoryId != null) setSelectedCategoryId(null);
      return;
    }
    if (selectedCategoryId != null && findCategoryNodeById(categoryTree, selectedCategoryId)) return;
    setSelectedCategoryId(categoryTree[0]?.id ?? null);
  }, [categoryTree, selectedCategoryId]);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!resizingRef.current || !splitRef.current) return;
      const rect = splitRef.current.getBoundingClientRect();
      const next = Math.min(PANEL_MAX, Math.max(PANEL_MIN, e.clientX - rect.left));
      setPanelWidth(next);
    };
    const onUp = () => {
      if (!resizingRef.current) return;
      resizingRef.current = false;
      setPanelResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      try {
        localStorage.setItem(PANEL_WIDTH_KEY, String(panelWidthRef.current));
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  const expandableIds = useMemo(() => collectExpandableCategoryIds(categoryTree), [categoryTree]);

  const categoryRows = useMemo(() => {
    const collapsed = isCategoryFilterActive ? new Set<number>() : collapsedIds;
    return flattenCategoryTree(filteredCategoryTree, collapsed);
  }, [filteredCategoryTree, collapsedIds, isCategoryFilterActive]);

  const selectedCategory = useMemo(
    () => (selectedCategoryId != null ? findCategoryNodeById(categoryTree, selectedCategoryId) : null),
    [categoryTree, selectedCategoryId],
  );

  const breadcrumb = useMemo(
    () => (selectedCategoryId != null ? getCategoryPath(categoryTree, selectedCategoryId) : []),
    [categoryTree, selectedCategoryId],
  );

  const linkedItems = useMemo(
    () => filterItemsForCategory(libraryItems, selectedCategory, includeSubcats, searchTerm),
    [libraryItems, selectedCategory, includeSubcats, searchTerm],
  );

  const isLoading = catsLoading || itemsLoading;

  const selectedIds = useMemo(
    () => new Set(selectedItems.map((item) => String(item.id))),
    [selectedItems],
  );
  const allVisibleSelected =
    linkedItems.length > 0 && linkedItems.every((item) => selectedIds.has(String(item.id)));
  const someVisibleSelected = linkedItems.some((item) => selectedIds.has(String(item.id)));

  useEffect(() => {
    const checkbox = selectAllRef.current;
    if (!checkbox) return;
    checkbox.indeterminate = someVisibleSelected && !allVisibleSelected;
  }, [allVisibleSelected, someVisibleSelected]);

  const selectionUnique = useMemo(() => {
    const unique: SpareItem[] = [];
    const seen = new Set<string>();
    for (const item of selectedItems) {
      const id = String(item.id);
      if (seen.has(id)) continue;
      seen.add(id);
      unique.push(item);
    }
    return unique;
  }, [selectedItems]);

  const insertableItems = useMemo(
    () => selectionUnique.filter((item) => !excludeItemIds?.has(String(item.id))),
    [excludeItemIds, selectionUnique],
  );
  const skippedDuplicates = selectionUnique.length - insertableItems.length;
  const estimatedAddedLines = insertableItems.length > 0 ? Math.max(0, insertableItems.length - 1) : 0;

  const toggleCollapsed = useCallback((id: number) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => setCollapsedIds(new Set()), []);
  const collapseAll = useCallback(() => setCollapsedIds(new Set(expandableIds)), [expandableIds]);

  const selectCategory = useCallback((id: number) => {
    setSelectedCategoryId(id);
    setSearchTerm('');
  }, []);

  const startResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    resizingRef.current = true;
    setPanelResizing(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  const toggleSelection = (item: SpareItem) => {
    setSelectedItems((prev) => {
      const id = String(item.id);
      const exists = prev.some((row) => String(row.id) === id);
      if (exists) return prev.filter((row) => String(row.id) !== id);
      return [...prev, item];
    });
  };

  const toggleSelectAllVisible = () => {
    setSelectedItems((prev) => {
      const pageIds = new Set(linkedItems.map((item) => String(item.id)));
      if (allVisibleSelected) {
        return prev.filter((item) => !pageIds.has(String(item.id)));
      }
      const next = [...prev];
      const existingIds = new Set(next.map((item) => String(item.id)));
      for (const item of linkedItems) {
        const id = String(item.id);
        if (!existingIds.has(id)) {
          next.push(item);
          existingIds.add(id);
        }
      }
      return next;
    });
  };

  const removeSelection = (itemId: string) => {
    setSelectedItems((prev) => prev.filter((row) => String(row.id) !== itemId));
  };

  const handleInsert = () => {
    if (insertableItems.length === 0) return;
    onInsert(insertableItems);
    onClose();
  };

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" zIndex={110} className="h-[min(88vh,52rem)]">
      <ModalHeader>
        <div className="flex w-full items-center justify-between gap-3">
          <ModalTitle>Find items</ModalTitle>
          <Button type="button" variant="ghost" size="icon" title="Close" aria-label="Close" onClick={onClose}>
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </Button>
        </div>
      </ModalHeader>
      <ModalContent className="flex min-h-0 flex-1 flex-col overflow-hidden !overflow-hidden p-0 !p-0">
        <div
          ref={splitRef}
          className={cn('flex min-h-0 flex-1 overflow-hidden border-y', colors.border)}
        >
          <aside
            className={cn('flex shrink-0 flex-col min-h-0 border-r', colors.border)}
            style={{ width: panelWidth }}
          >
            <div className={cn('space-y-2 border-b px-3 py-2.5', colors.border, colors.surfaceHover)}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className={cn('text-xs font-semibold uppercase tracking-wider', colors.textMuted)}>Catalog</p>
                  <p className={cn('truncate text-[11px]', colors.textMuted)}>
                    {libraries.find((l) => l.id === libraryId)?.name ?? 'Select library'}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-0.5">
                  <button
                    type="button"
                    onClick={expandAll}
                    disabled={isCategoryFilterActive || expandableIds.length === 0}
                    className={cn(
                      'inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors',
                      colors.textMuted,
                      colors.interactiveHover,
                      'disabled:pointer-events-none disabled:opacity-40',
                    )}
                    title="Expand all"
                    aria-label="Expand all"
                  >
                    <ExpandAllIcon className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={collapseAll}
                    disabled={isCategoryFilterActive || expandableIds.length === 0}
                    className={cn(
                      'inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors',
                      colors.textMuted,
                      colors.interactiveHover,
                      'disabled:pointer-events-none disabled:opacity-40',
                    )}
                    title="Collapse all"
                    aria-label="Collapse all"
                  >
                    <CollapseAllIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

              <select
                className={cn(
                  'h-8 w-full rounded-md border px-2 text-sm',
                  colors.border,
                  colors.surface,
                  colors.text,
                  'focus:outline-none focus:ring-2 focus:ring-indigo-500/40',
                )}
                value={libraryId ?? ''}
                onChange={(e) => {
                  const next = e.target.value === '' ? null : Number(e.target.value);
                  setLibraryId(next);
                  setSearchTerm('');
                }}
                aria-label="Spare library"
              >
                {libraries.length === 0 ? <option value="">No libraries</option> : null}
                {libraries.map((lib) => (
                  <option key={lib.id} value={lib.id}>
                    {lib.name}
                  </option>
                ))}
              </select>

              <div className="relative">
                <SearchIcon
                  className={cn(
                    'pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2',
                    colors.textMuted,
                  )}
                />
                <input
                  type="search"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  placeholder="Filter folders…"
                  disabled={libraryId == null}
                  className={cn(
                    'h-8 w-full rounded-md border pl-8 pr-3 text-sm',
                    colors.border,
                    colors.surface,
                    colors.text,
                    'focus:outline-none focus:ring-2 focus:ring-indigo-500/40',
                    'disabled:opacity-50',
                  )}
                />
              </div>
            </div>

            <div className="dashboard-scrollbar flex-1 overflow-y-auto py-1">
              {libraryId == null && (
                <p className={cn('px-3 py-6 text-center text-sm', colors.textMuted)}>Create a spare library first.</p>
              )}
              {libraryId != null && catsLoading && (
                <p className={cn('px-3 py-4 text-sm', colors.textMuted)}>Loading folders…</p>
              )}
              {libraryId != null && !catsLoading && categoryTree.length === 0 && (
                <p className={cn('px-3 py-6 text-center text-sm', colors.textMuted)}>No folders yet.</p>
              )}
              {libraryId != null && !catsLoading && categoryTree.length > 0 && categoryRows.length === 0 && (
                <p className={cn('px-3 py-6 text-center text-sm', colors.textMuted)}>No folders match.</p>
              )}
              {libraryId != null &&
                !catsLoading &&
                categoryRows.map((row: CategoryTreeRow) => {
                  const active = selectedCategoryId === row.id;
                  const isExpanded = row.hasChildren && !collapsedIds.has(row.id);
                  const itemCount = countDirectItemsInCategory(libraryItems, row.id);
                  return (
                    <div
                      key={row.id}
                      role="treeitem"
                      aria-selected={active}
                      aria-expanded={row.hasChildren ? isExpanded : undefined}
                      onClick={() => selectCategory(row.id)}
                      className={cn(
                        'group/row flex h-9 cursor-pointer select-none items-center gap-1 border-l-2 transition-colors',
                        active
                          ? cn(colors.primary, colors.primaryText, colors.primaryBorder)
                          : cn('border-transparent', colors.text, colors.interactiveHover),
                        !row.isActive && !active && 'opacity-60',
                      )}
                      style={{ paddingLeft: 8 + row.depth * 14, paddingRight: 6 }}
                      title={row.name}
                    >
                      <span className="flex w-5 shrink-0 items-center justify-center">
                        {row.hasChildren ? (
                          <button
                            type="button"
                            className={cn(
                              '-m-0.5 inline-flex h-6 w-6 items-center justify-center rounded',
                              active ? 'text-white/85 hover:bg-white/15' : cn(colors.textMuted, colors.interactiveHover),
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleCollapsed(row.id);
                            }}
                            title={isExpanded ? 'Collapse' : 'Expand'}
                            aria-label={isExpanded ? 'Collapse' : 'Expand'}
                          >
                            {isExpanded ? (
                              <ChevronDownIcon className="h-3.5 w-3.5" />
                            ) : (
                              <ChevronRightIcon className="h-3.5 w-3.5" />
                            )}
                          </button>
                        ) : (
                          <span className="w-6" aria-hidden />
                        )}
                      </span>
                      <span
                        className={cn(
                          'inline-flex w-5 shrink-0 items-center justify-center',
                          active ? 'text-white/90' : colors.textMuted,
                        )}
                        aria-hidden
                      >
                        <FolderIcon className="h-3.5 w-3.5" />
                      </span>
                      <span className={cn('min-w-0 flex-1 truncate text-sm', active && 'font-medium')}>
                        {row.name}
                      </span>
                      <span
                        className={cn(
                          'min-w-[1.25rem] shrink-0 text-right text-[11px] tabular-nums',
                          active ? 'text-white/70' : colors.textMuted,
                        )}
                        title={`${itemCount} direct item${itemCount === 1 ? '' : 's'}`}
                      >
                        {itemCount}
                      </span>
                    </div>
                  );
                })}
            </div>
          </aside>

          <div
            role="separator"
            aria-orientation="vertical"
            aria-valuenow={panelWidth}
            aria-label="Resize folders panel"
            title="Drag to resize"
            onMouseDown={startResize}
            className={cn(
              'group flex w-1.5 shrink-0 cursor-col-resize items-center justify-center',
              'hover:bg-sky-500/20 active:bg-sky-500/30',
              'border-l',
              colors.border,
              panelResizing && 'bg-sky-500/30',
            )}
          >
            <div className={cn('h-12 w-0.5 rounded-full opacity-60 group-hover:opacity-100', colors.textMuted)} />
          </div>

          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <div
              className={cn(
                'flex flex-shrink-0 flex-col gap-2.5 border-b px-4 py-3',
                colors.border,
                colors.surfaceHover,
              )}
            >
              <div className="min-w-0">
                {breadcrumb.length > 0 ? (
                  <nav className="flex min-w-0 flex-wrap items-center gap-1 text-sm" aria-label="Category path">
                    {breadcrumb.map((crumb, index) => {
                      const isLast = index === breadcrumb.length - 1;
                      return (
                        <React.Fragment key={crumb.id}>
                          {index > 0 && (
                            <span className={cn('shrink-0', colors.textMuted)} aria-hidden>
                              /
                            </span>
                          )}
                          {isLast ? (
                            <span className={cn('truncate font-semibold', colors.text)} title={crumb.name}>
                              {crumb.name}
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => selectCategory(crumb.id)}
                              className={cn('truncate hover:underline', colors.textMuted)}
                              title={crumb.name}
                            >
                              {crumb.name}
                            </button>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </nav>
                ) : (
                  <p className={cn('text-sm font-semibold', colors.text)}>Select a folder</p>
                )}
                <p className={cn('mt-0.5 text-xs', colors.textMuted)}>
                  {selectedCategory
                    ? `${linkedItems.length} item${linkedItems.length === 1 ? '' : 's'}${
                        includeSubcats ? ' (including subfolders)' : ''
                      }`
                    : 'Choose a folder on the left'}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <div className="relative min-w-[12rem] flex-1">
                  <SearchIcon
                    className={cn(
                      'pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2',
                      colors.textMuted,
                    )}
                  />
                  <input
                    type="search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search name, vendor code, drawing, serial…"
                    disabled={!selectedCategory}
                    className={cn(
                      'h-8 w-full rounded-md border pl-8 pr-3 text-sm',
                      colors.border,
                      colors.surface,
                      colors.text,
                      'focus:outline-none focus:ring-2 focus:ring-indigo-500/40',
                      'disabled:opacity-50',
                    )}
                  />
                </div>
                <div
                  className={cn('inline-flex rounded-md border p-0.5 text-xs', colors.border, colors.surface)}
                  role="group"
                  aria-label="Folder scope"
                >
                  <button
                    type="button"
                    onClick={() => setIncludeSubcats(false)}
                    className={cn(
                      'h-7 rounded px-2.5 transition-colors',
                      !includeSubcats
                        ? cn(colors.primary, colors.primaryText)
                        : cn(colors.textMuted, colors.interactiveHover),
                    )}
                  >
                    This folder
                  </button>
                  <button
                    type="button"
                    onClick={() => setIncludeSubcats(true)}
                    className={cn(
                      'h-7 rounded px-2.5 transition-colors',
                      includeSubcats
                        ? cn(colors.primary, colors.primaryText)
                        : cn(colors.textMuted, colors.interactiveHover),
                    )}
                  >
                    + Subfolders
                  </button>
                </div>
              </div>
            </div>

            {selectedItems.length > 0 ? (
              <div className={cn('flex-shrink-0 border-b px-3 py-2', colors.border)}>
                <div className="mb-1.5 flex items-center justify-between gap-2">
                  <p className={cn('text-xs font-medium', colors.textSecondary)}>
                    Selected ({selectedItems.length})
                  </p>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setSelectedItems([])}>
                    Clear selected
                  </Button>
                </div>
                <div className="max-h-20 space-y-1 overflow-auto">
                  {selectedItems.map((item) => (
                    <div
                      key={`sel-${item.id}`}
                      className={cn('flex items-center justify-between rounded px-2 py-1 text-xs', colors.surfaceHover)}
                    >
                      <span className={cn('truncate', colors.text)}>
                        {item.name || '—'}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        title="Remove selected item"
                        aria-label="Remove selected item"
                        onClick={() => removeSelection(String(item.id))}
                      >
                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="min-h-0 flex-1 overflow-auto dashboard-scrollbar">
              {isLoading ? (
                <div className={cn('flex flex-col items-center justify-center gap-2 py-16 text-sm', colors.textMuted)}>
                  <div className={cn('h-5 w-5 animate-spin rounded-full border-2 border-t-transparent', colors.border)} />
                  Loading items…
                </div>
              ) : linkedItems.length === 0 ? (
                <div
                  className={cn(
                    'm-2 flex flex-col items-center justify-center rounded-xl border border-dashed px-6 py-16 text-center',
                    colors.border,
                    colors.surfaceHover,
                  )}
                >
                  <p className={cn('text-sm font-medium', colors.text)}>No spare items</p>
                  <p className={cn('mt-1 max-w-sm text-xs leading-relaxed', colors.textMuted)}>
                    {!selectedCategory
                      ? 'Select a folder to view items.'
                      : searchTerm.trim()
                        ? 'No items match your search in this folder.'
                        : includeSubcats
                          ? 'No spare items in this folder or its subfolders.'
                          : 'No direct items here. Switch to “+ Subfolders” or pick a child folder.'}
                  </p>
                </div>
              ) : (
                <table className={cn('w-full table-fixed text-sm', colors.border)}>
                  <thead className={cn('sticky top-0 z-10 border-b backdrop-blur-sm', colors.border, colors.surface)}>
                    <tr>
                      <th className="w-10 px-2 py-2.5 text-left">
                        <input
                          ref={selectAllRef}
                          type="checkbox"
                          checked={allVisibleSelected}
                          onChange={toggleSelectAllVisible}
                          className="rounded"
                          title="Select all visible"
                          aria-label="Select all visible"
                        />
                      </th>
                      <th
                        className={cn(
                          'px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider',
                          colors.textMuted,
                        )}
                      >
                        Item
                      </th>
                      <th
                        className={cn(
                          'hidden w-28 px-2 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider lg:table-cell',
                          colors.textMuted,
                        )}
                      >
                        Drawing
                      </th>
                      <th
                        className={cn(
                          'w-16 px-2 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider',
                          colors.textMuted,
                        )}
                      >
                        Files
                      </th>
                      <th
                        className={cn(
                          'w-28 px-2 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider',
                          colors.textMuted,
                        )}
                      >
                        Flags
                      </th>
                      <th className="w-14 px-2 py-2.5 pr-4 text-right">
                        <span className="sr-only">View</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {linkedItems.map((row) => {
                      const meta = [row.vendorCode, row.serialNo && `S/N ${row.serialNo}`].filter(Boolean).join(' · ');
                      const fileCount =
                        row.fileCount ?? row._count?.attachments ?? row.FileAttachment?.length ?? row.attachments?.length ?? 0;
                      const checked = selectedIds.has(String(row.id));
                      return (
                        <tr
                          key={row.id}
                          className={cn(
                            'group border-b transition-colors',
                            colors.border,
                            !row.isActive && 'opacity-55',
                            colors.interactiveHover,
                          )}
                        >
                          <td className="px-2 py-2.5 align-middle">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleSelection(row)}
                              className="rounded"
                              aria-label={`Select ${row.name || row.vendorCode || row.id}`}
                            />
                          </td>
                          <td className="px-3 py-2.5 align-middle">
                            <span className={cn('block truncate font-medium', colors.text)} title={row.name}>
                              {row.name}
                            </span>
                            {meta ? (
                              <span className={cn('mt-0.5 block truncate text-xs tabular-nums', colors.textMuted)} title={meta}>
                                {meta}
                              </span>
                            ) : null}
                          </td>
                          <td className={cn('hidden px-2 py-2.5 align-middle tabular-nums lg:table-cell', colors.textSecondary)}>
                            <span className="block truncate" title={row.drawingNo ?? undefined}>
                              {row.drawingNo || '—'}
                            </span>
                          </td>
                          <td className={cn('px-2 py-2.5 align-middle tabular-nums', colors.textSecondary)}>
                            {fileCount > 0 ? (
                              <span className={cn('font-medium', colors.text)}>{fileCount}</span>
                            ) : (
                              <span className={colors.textMuted}>—</span>
                            )}
                          </td>
                          <td className="px-2 py-2.5 align-middle">
                            <div className="flex flex-wrap items-center gap-1">
                              {row.isCritical ? <FlagChip label="Crit" tone="warn" /> : null}
                              {row.isHazardousMaterial ? <FlagChip label="Haz" tone="danger" /> : null}
                              {!row.isCritical && !row.isHazardousMaterial ? (
                                <span className={colors.textMuted}>—</span>
                              ) : null}
                            </div>
                          </td>
                          <td className="px-2 py-2.5 pr-4 align-middle text-right">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              title="View item"
                              aria-label="View item"
                              onClick={(e) => {
                                e.stopPropagation();
                                setViewItemId(row.id);
                              }}
                            >
                              <EyeIcon className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </ModalContent>
      <ModalFooter className="sticky bottom-0">
        <div className="flex w-full items-center justify-between gap-3">
          <div className={cn('text-xs', colors.textMuted)}>
            <span className={colors.text}>{selectedItems.length} selected</span>
            {' • '}
            <span>estimated +{estimatedAddedLines} lines</span>
            {skippedDuplicates > 0 ? (
              <span className={cn('ml-2', colors.warning || colors.textMuted)}>
                ({skippedDuplicates} duplicate{skippedDuplicates === 1 ? '' : 's'} skipped)
              </span>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={onClose}>
              Close
            </Button>
            <Button
              type="button"
              variant="default"
              size="sm"
              disabled={insertableItems.length === 0}
              onClick={handleInsert}
            >
              Insert selected ({insertableItems.length})
            </Button>
          </div>
        </div>
      </ModalFooter>
    </Modal>
    <SpareItemDetailModal
      isOpen={viewItemId != null}
      itemId={viewItemId}
      onClose={() => setViewItemId(null)}
      zIndex={120}
    />
    </>
  );
};
